import type { MfgBuildPipelinePhase } from "@/lib/manufacturing/buildPipelineTypes";

const PROPOSED_TILES = [
  { label: "QC inspection", sub: "QcInspection / submitQcPass" },
  { label: "Production photos", sub: "ProductionPhoto" },
  { label: "Assigned crew", sub: "WorkstationAssignment" },
  { label: "Schedule / priority", sub: "scheduledFor · queueSequence" },
] as const;

export type BuildPipelineHistoryRow = {
  id: string;
  from_phase_id: string | null;
  to_phase_id: string;
  moved_at: string;
  notes: string;
  moved_by_user_id?: string | null;
  moved_by_display_name?: string | null;
};

export function BuildPipelineUnitPhaseHistorySection({
  history,
  phaseById,
}: {
  history: readonly BuildPipelineHistoryRow[];
  phaseById: Record<string, MfgBuildPipelinePhase | undefined>;
}) {
  return (
    <section>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        Phase history
      </h3>
      {history.length === 0 ? (
        <p className="text-xs text-gray-500">No transitions yet.</p>
      ) : (
        <ul className="space-y-1.5 text-xs">
          {history.map((h) => (
            <li key={h.id} className="text-gray-700 dark:text-zinc-300">
              {h.moved_by_display_name ? (
                <span className="font-medium text-gray-900 dark:text-zinc-100">
                  {h.moved_by_display_name}
                </span>
              ) : null}
              {h.moved_by_display_name ? <span className="text-gray-400"> · </span> : null}
              <span className="text-gray-500">
                {h.from_phase_id
                  ? phaseById[h.from_phase_id]?.display_name || "—"
                  : "—"}
              </span>
              {" → "}
              <span>{phaseById[h.to_phase_id]?.display_name || h.to_phase_id.slice(0, 8)}</span>
              {h.notes ? (
                <span className="ml-1 italic text-gray-500">“{h.notes}”</span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function BuildPipelineUnitProposedFloorSection() {
  return (
    <section>
      <div className="mb-2 flex items-center gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          On the floor
        </h3>
        <span className="rounded border border-dashed border-slate-400 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-slate-500">
          proposed
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {PROPOSED_TILES.map((t) => (
          <div
            key={t.label}
            className="rounded-lg border border-dashed border-slate-300 px-2.5 py-2 opacity-70 dark:border-slate-600"
            title="Not wired — backend slice pending"
          >
            <div className="text-xs font-medium text-slate-600 dark:text-slate-300">
              {t.label}
            </div>
            <div className="mt-0.5 font-mono text-[9px] text-slate-400">{t.sub}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
